import { ADVENTURE_REWARD_ICONS, type AdventureBoxReward } from "../../adventure/adventure_box_rewards.js";
import { formatMinutesShort, isTimedRewardType, rewardAmountText } from "../../common/reward_format.js";
import { Game } from "../../game.js";
import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AdventureTooltip } from "./adventure_tooltip.js";

/** A rectangle in game-canvas pixels (x from the left edge of the game area). */
export interface TooltipAnchorRect
{
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface RewardsTooltipOptions
{
  /** The bubble grows to the left of the anchor (a chest at the right edge of a row). */
  align_left?: boolean;
  /** Extra vertical shift in design px, positive moves it down. */
  offset_y?: number;
}

/**
 * A tooltip painted once into its own fixed canvas on top of the page. It is
 * for anchors that live in DOM layers, like the CSS-scrolled tournament
 * standings (z-index 120 over the dialog canvas): AnchoredTooltip and
 * TooltipManager draw into game canvases that sit underneath those layers.
 * One is open at a time; the next pointer-down anywhere closes it.
 */
export class DomTooltip
{
  private static readonly Z_INDEX = 130;
  private static readonly SCREEN_MARGIN = 10;

  // The name bubble matches AnchoredTooltip's compact variant.
  private static readonly TEXT_MIN_W = 320;
  private static readonly TEXT_MAX_W = 720;
  private static readonly TEXT_CHAR_W = 30;
  private static readonly TEXT_PAD_X = 60;
  private static readonly TEXT_PAD_Y = 22;
  private static readonly TEXT_H = 150;
  private static readonly TEXT_FONT = 46;
  private static readonly TEXT_MIN_FONT = 28;
  private static readonly TEXT_TAIL_W = 67;
  private static readonly TEXT_TAIL_H = 47;
  /** How far the tail tucks under the bubble. */
  private static readonly TEXT_TAIL_OVERLAP = 19;
  /** The tail tip dips this far into the anchor. */
  private static readonly TEXT_ANCHOR_DIP = 38;
  private static readonly TEXT_ANCHOR_GAP = 8;
  /** Closest the tail gets to the bubble's side. */
  private static readonly TEXT_TAIL_EDGE = 16;

  private static readonly REWARDS_TAIL_EXTENSION = 46;
  private static readonly REWARDS_TAIL_MARGIN = 70;
  private static readonly REWARDS_ANCHOR_DIP = 8;

  private static opened: HTMLCanvasElement | null = null;
  private static readonly dismiss = (): void => DomTooltip.hide();

  /** A short name above the anchor, or below it when there is no room. */
  public static showText( anchor: TooltipAnchorRect, text: string ): void
  {
    const T = DomTooltip;
    const w = Math.min( LayoutScaler.widthFromDesign( Math.min( T.TEXT_MAX_W, Math.max( T.TEXT_MIN_W, text.length * T.TEXT_CHAR_W + T.TEXT_PAD_X * 2 ) ) ),
      Game.screenW() - T.SCREEN_MARGIN * 2 );
    const h = LayoutScaler.heightFromDesign( T.TEXT_H );
    const tail_w = LayoutScaler.widthFromDesign( T.TEXT_TAIL_W );
    const tail_h = LayoutScaler.heightFromDesign( T.TEXT_TAIL_H );
    const tail_out = tail_h - LayoutScaler.heightFromDesign( T.TEXT_TAIL_OVERLAP );
    const dip = LayoutScaler.heightFromDesign( T.TEXT_ANCHOR_DIP );

    const center_x = anchor.x + anchor.w / 2;
    const left = T.clampX( center_x - w / 2, w );
    const above_top = anchor.y - h - tail_out - T.TEXT_ANCHOR_GAP + dip;
    const below = above_top < T.SCREEN_MARGIN;
    const top = below ? anchor.y + anchor.h + T.TEXT_ANCHOR_GAP + dip : above_top;
    const tail_x = Math.max( T.TEXT_TAIL_EDGE, Math.min( center_x - left - tail_w / 2, w - tail_w - T.TEXT_TAIL_EDGE ) );

    T.mount( left, top, w, h + tail_out, ctx =>
    {
      const root = new UIElementHolder( ctx, 0, 0, w, h + tail_out );
      const bubble_y = below ? tail_out : 0;
      const substrate = new UINineSliceElement( ctx, 0, bubble_y, w, h, ImgID.UI_TutorialSubstrate, "tutorial_substrate" );
      const pad_x = LayoutScaler.widthFromDesign( T.TEXT_PAD_X );
      const pad_y = LayoutScaler.heightFromDesign( T.TEXT_PAD_Y );
      substrate.addChild( new UITextElement( ctx, pad_x, pad_y, w - pad_x * 2, h - pad_y * 2, {
        text,
        font_size: LayoutScaler.textSizeFromDesign( T.TEXT_FONT ),
        min_font_size: LayoutScaler.textSizeFromDesign( T.TEXT_MIN_FONT ),
        font_weight: 900,
        text_color: ColorScheme.anchored_tooltip_text,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: 0,
        fit_font_size: true,
        auto_wrap: false,
      } ), true );
      root.addChild( substrate, true );
      root.addChild( below
        ? new UIImage( ctx, tail_x, 0, tail_w, tail_h, ImgID.UI_TooltipArrow, { rotation_angle: 180 } )
        : new UIImage( ctx, tail_x, h - LayoutScaler.heightFromDesign( T.TEXT_TAIL_OVERLAP ), tail_w, tail_h, ImgID.UI_TooltipArrow ), true );
      return root;
    } );
  }

  /** The rewards grid (AdventureTooltip) above the anchor, tail pointing at its centre. */
  public static showRewards( anchor: TooltipAnchorRect, rewards: readonly AdventureBoxReward[], options: RewardsTooltipOptions = {} ): void
  {
    const T = DomTooltip;
    if ( rewards.length === 0 )
      return;

    const content = rewards.map( reward => ( {
      img_id: ADVENTURE_REWARD_ICONS[reward.type],
      count: reward.amount,
      amount_text: reward.type === "coin" ? String( reward.amount )
        : isTimedRewardType( reward.type ) ? formatMinutesShort( reward.amount )
        : rewardAmountText( reward.type, reward.amount ),
    } ) );
    const { width, height } = AdventureTooltip.computeSize( content.length );
    const tail_margin = LayoutScaler.widthFromDesign( T.REWARDS_TAIL_MARGIN );
    const tail_extension = LayoutScaler.heightFromDesign( T.REWARDS_TAIL_EXTENSION );

    const anchor_x = anchor.x + anchor.w / 2;
    const left = T.clampX( options.align_left ? anchor_x - width + tail_margin : anchor_x - width / 2, width );
    const top = Math.max( T.SCREEN_MARGIN, anchor.y - height
      + LayoutScaler.heightFromDesign( T.REWARDS_ANCHOR_DIP + ( options.offset_y ?? 0 ) ) );
    const tail_x = Math.max( tail_margin, Math.min( width - tail_margin, anchor_x - left ) );

    T.mount( left, top, width, height + tail_extension, ctx => new AdventureTooltip( ctx, 0, 0, width, height, tail_x, content ) );
  }

  public static hide(): void
  {
    document.removeEventListener( "pointerdown", DomTooltip.dismiss, true );
    DomTooltip.opened?.remove();
    DomTooltip.opened = null;
  }

  private static clampX( left: number, w: number ): number
  {
    return Math.max( DomTooltip.SCREEN_MARGIN, Math.min( left, Game.screenW() - w - DomTooltip.SCREEN_MARGIN ) );
  }

  /** Paints `build`'s element once into a fixed canvas at game-area position (left, top). */
  private static mount( left: number, top: number, w: number, h: number, build: ( ctx: CanvasRenderingContext2D ) => UIElement ): void
  {
    DomTooltip.hide();

    const dpr = Game.getDpr();
    const canvas = document.createElement( "canvas" );
    canvas.width = Math.ceil( w * dpr );
    canvas.height = Math.ceil( h * dpr );
    canvas.style.cssText = [
      "position:fixed", `left:${Game.getGameX() + left}px`, `top:${top}px`,
      `width:${w}px`, `height:${h}px`, "pointer-events:none", `z-index:${DomTooltip.Z_INDEX}`,
    ].join( ";" );

    const ctx = canvas.getContext( "2d" );
    if ( !ctx )
      return;
    ctx.setTransform( dpr, 0, 0, dpr, 0, 0 );
    build( ctx ).render();

    document.body.appendChild( canvas );
    DomTooltip.opened = canvas;
    document.addEventListener( "pointerdown", DomTooltip.dismiss, true );
  }
}
